// Code local à six chiffres (D-029) : défini à la première ouverture de l'application terrain sur
// l'appareil, demandé à chaque réouverture, y compris hors connexion. Aucun secret serveur ; le
// code protège seulement les données enregistrées sur l'appareil.
import { Bandeau, Bouton, Champ, ConfirmationIntegree, Formulaire, IndicateurEnvironnement, type ErreurChamp } from '@cc/ui';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../../session/FournisseurSession';
import { codeValide, definirCodeLocal, deverrouiller, fermerSessionLocale, verifierCodeLocal } from '../../terrain/session-locale';
import { supprimerBase } from '../../terrain/stockage';
import { useSynchronisation } from '../../terrain/useTerrain';
import { useZoneTerrain } from './CoquilleTerrain';
import styles from './terrain.module.css';

export function PageCodeLocal({ mode, onFait }: { mode: 'definir' | 'saisir'; onFait: () => void }) {
  const { session, modeLocal } = useZoneTerrain();
  const { deconnecter } = useSession();
  const sync = useSynchronisation();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreurs, setErreurs] = useState<ErreurChamp[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const champ = useRef<HTMLInputElement>(null);
  const titre = mode === 'definir' ? 'Définir le code local' : 'Saisir le code local';

  useEffect(() => {
    document.title = `${titre} - Terrain - CollectivitésConnect`;
    champ.current?.focus();
  }, [titre]);

  const soumettre = async () => {
    const valeur = code.replace(/\s/g, '');
    const fautes: ErreurChamp[] = [];
    if (!codeValide(valeur)) fautes.push({ champ: 'code-local', message: 'Le code comporte six chiffres' });
    if (mode === 'definir' && confirmation.replace(/\s/g, '') !== valeur) fautes.push({ champ: 'code-local-confirmation', message: 'Les deux codes ne sont pas identiques' });
    setErreurs(fautes);
    if (fautes.length > 0) return;
    setErreur(null);
    setEnCours(true);
    try {
      if (mode === 'definir') {
        await definirCodeLocal(valeur);
        deverrouiller(session.compte_id);
        onFait();
      } else if (await verifierCodeLocal(valeur)) {
        deverrouiller(session.compte_id);
        onFait();
      } else {
        setErreur('Code incorrect.');
        setCode('');
        champ.current?.focus();
      }
    } finally {
      setEnCours(false);
    }
  };

  const attente = sync.attente.operations + sync.attente.photos;
  const quitter = async () => {
    await fermerSessionLocale();
    await supprimerBase();
    await deconnecter();
    navigate('/connexion', { replace: true });
  };

  return (
    <div className={styles.coquille}>
      <main className={styles.pageVerrou} id="contenu-principal">
        <div className={styles.marque}>
          <img src="/marque-collectivitesconnect.png" alt="" width={26} height={22} className="cc-logo-jour" />
          <img src="/marque-collectivitesconnect-fond-sombre.png" alt="" width={26} height={22} className="cc-logo-nuit" />
          <span className={styles.marqueTexte}>
            <strong>Terrain</strong>
            <small>{session.collectivite ?? 'CollectivitésConnect'}</small>
          </span>
        </div>
        <h1 className={styles.titrePage}>{titre}</h1>
        <p className={styles.texte}>
          {session.nom} ({session.identifiant})
        </p>
        {mode === 'definir' ? (
          <p className={styles.aide}>Ce code à six chiffres protège les missions, rapports et photos enregistrés sur cet appareil. Il est demandé à chaque ouverture de l&apos;application terrain, même sans réseau, pendant trente jours renouvelés à chaque synchronisation.</p>
        ) : (
          <p className={styles.aide}>{modeLocal ? 'Hors connexion ou session serveur à rouvrir : les données de cet appareil restent accessibles avec le code local.' : 'Le code local a été défini sur cet appareil à la première ouverture.'}</p>
        )}
        {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
        <Formulaire
          libelle={titre}
          onSoumettre={() => void soumettre()}
          erreurs={erreurs}
          actions={
            <Bouton type="submit" chargement={enCours} libelleChargement="Vérification en cours" pleineLargeur>
              {mode === 'definir' ? 'Enregistrer le code' : 'Déverrouiller'}
            </Bouton>
          }
        >
          <Champ id="code-local" libelle="Code à six chiffres" required inputMode="numeric" autoComplete="off" pattern="[0-9]*" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} erreur={erreurs.find((f) => f.champ === 'code-local')?.message} ref={champ} className={styles.codeChamp} />
          {mode === 'definir' ? <Champ id="code-local-confirmation" libelle="Confirmer le code" required inputMode="numeric" autoComplete="off" pattern="[0-9]*" maxLength={6} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} erreur={erreurs.find((f) => f.champ === 'code-local-confirmation')?.message} className={styles.codeChamp} /> : null}
        </Formulaire>
        <div className={styles.pile}>
          {attente > 0 ? <p className={styles.aide}>{attente} élément(s) enregistrés sur cet appareil attendent la synchronisation : se déconnecter les effacerait.</p> : null}
          <ConfirmationIntegree libelle="Se déconnecter et effacer les données de cet appareil" question="Les missions, rapports et photos enregistrés sur cet appareil seront effacés. Continuer ?" libelleConfirmer="Effacer et se déconnecter" onConfirmer={() => void quitter()} petit />
        </div>
        <div className={styles.pied}>
          <IndicateurEnvironnement environnement={import.meta.env.VITE_CC_ENVIRONNEMENT} />
        </div>
      </main>
    </div>
  );
}
